import { useMemo } from "react";

import type { Profession } from "@/models/ProfessionModel.ts";
import { PatientListColumnId, type PatientListColumnOverride } from "@/models/UISettingsModel.ts";
import { Queries } from "@/queries/Queries.ts";
import type { PatientListMenuItems } from "@/uiKit/organisms/PatientsList/PatientListMenu.tsx";

export type PatientListRole = "nurse" | "doctor";

export const columnLabelKey: Record<PatientListColumnId, string> = {
  patientNameForNurse: "pages.patients.columns.patientName",
  patientNameForDoctor: "pages.patients.columns.patientName",
  diabetes: "pages.patients.diabetesType",
  telemonitoring: "pages.patients.telemonitoring",
  noticesForNurse: "pages.patients.notifications",
  noticesForDoctor: "pages.patients.notifications",
  messagesForNurse: "pages.patients.messages",
  messagesForDoctor: "pages.patients.messages",
  referentDoctor: "pages.patients.referentDoctor",
  tlsNurse: "pages.patients.tlsNurse",
  lastSeen: "pages.patients.lastSeen.header",
};

export const mandatoryColumns: Record<PatientListRole, PatientListColumnId[]> = {
  nurse: ["patientNameForNurse"],
  doctor: ["patientNameForDoctor"],
};

export const defaultColumns: Record<
  PatientListRole | "nurseMultipleDoc",
  Record<PatientListMenuItems, PatientListColumnId[]>
> = {
  nurse: {
    monitored: ["patientNameForNurse", "lastSeen", "messagesForNurse", "noticesForNurse"],
    pregnant: ["patientNameForNurse", "diabetes", "lastSeen", "messagesForNurse", "noticesForNurse"],
    all: ["patientNameForNurse", "telemonitoring", "diabetes", "messagesForNurse", "noticesForNurse"],
  },
  nurseMultipleDoc: {
    monitored: ["patientNameForNurse", "referentDoctor", "lastSeen", "messagesForNurse", "noticesForNurse"],
    pregnant: ["patientNameForNurse", "diabetes", "referentDoctor", "lastSeen", "messagesForNurse", "noticesForNurse"],
    all: ["patientNameForNurse", "telemonitoring", "diabetes", "referentDoctor", "messagesForNurse", "noticesForNurse"],
  },
  doctor: {
    monitored: ["patientNameForDoctor", "messagesForDoctor", "noticesForDoctor"],
    pregnant: ["patientNameForDoctor", "diabetes", "messagesForDoctor", "noticesForDoctor"],
    all: ["patientNameForDoctor", "telemonitoring", "diabetes", "messagesForDoctor", "noticesForDoctor"],
  },
};

export const availableColumns: Record<PatientListRole, PatientListColumnId[]> = {
  nurse: [
    "patientNameForNurse",
    "messagesForNurse",
    "noticesForNurse",
    "lastSeen",
    "referentDoctor",
    "diabetes",
    "telemonitoring",
  ],
  doctor: [
    "patientNameForDoctor",
    "messagesForDoctor",
    "noticesForDoctor",
    "lastSeen",
    "tlsNurse",
    "diabetes",
    "telemonitoring",
  ],
};

export type ColumnVisibilityState = Partial<Record<PatientListColumnId, boolean>>;

export type UseSelectPatientsListColumnsReturn = {
  displayedColumns: PatientListColumnId[];
  availableColumns: PatientListColumnId[];
  visibilityState: ColumnVisibilityState;
  mandatoryColumns: PatientListColumnId[];
  defaultColumns: PatientListColumnId[];
  overrides: PatientListColumnOverride;
};

export const useSelectPatientsListColumns = (options: {
  qualification: Profession;
  hasMultipleDoctors?: boolean;
  tab: PatientListMenuItems;
}): UseSelectPatientsListColumnsReturn => {
  const me = Queries.user.usePractitionerMe({});
  const { qualification, tab, hasMultipleDoctors } = options;
  const visibilityPrefs = Queries.user.usePatientListColumnVisibility({ userId: me.id });

  return useMemo(() => {
    const roleAvailable = availableColumns[qualification];
    const columnDefaultRole = qualification === "nurse" && hasMultipleDoctors ? "nurseMultipleDoc" : qualification;
    const tabDefaults = defaultColumns[columnDefaultRole][tab];
    const tabOverrides = visibilityPrefs[tab] ?? {};
    const mandatory = mandatoryColumns[qualification];
    const displayedColumns = roleAvailable.filter(
      col =>
        mandatory.includes(col) ||
        (tabOverrides[col] !== undefined && tabOverrides[col]) ||
        (tabOverrides[col] === undefined && tabDefaults.includes(col)),
    );
    const visibilityState: ColumnVisibilityState = Object.fromEntries(
      roleAvailable.map(col => [col, displayedColumns.includes(col)]),
    );
    return {
      displayedColumns,
      availableColumns: roleAvailable,
      visibilityState,
      mandatoryColumns: mandatory,
      defaultColumns: tabDefaults,
      overrides: tabOverrides,
    };
  }, [qualification, hasMultipleDoctors, visibilityPrefs, tab]);
};
